import type { ChapterVisual, PeerOption } from '../types/pulse';

// ALL percentages below are ILLUSTRATIVE demonstration data — never genuine survey findings.
// Respondent counts are deliberately left as [N] / [n] placeholders.

export const resultsMedia = {
  cover: 'https://cdn.magicpatterns.com/patterns/generated-images/ed1ac983-1da8-4c8b-8121-a67f13a430f6.jpg',
  profiles: 'https://cdn.magicpatterns.com/patterns/generated-images/b4fd7eff-7d13-4812-9b96-6873cebe3c3e.jpg',
  balance: 'https://cdn.magicpatterns.com/patterns/generated-images/3ef42559-1815-4ae3-9bf5-d7b334e824bf.jpg',
  monitoring: 'https://cdn.magicpatterns.com/patterns/generated-images/baeb3a9f-74f5-489f-9268-da20da8d986c.jpg',
};

export const resultsCover = {
  eyebrow: 'IVF Pulse · Results',
  headline: 'Same science.',
  headlineAccent: 'Different perspectives.',
  subline:
    'We asked fertility specialists about ovarian stimulation. Their answers reveal where opinions align — and where they don’t.',
  participants: 'Based on responses from [N] participating fertility professionals.',
  teaser: ['The first Pulse asked what you think.', 'This one explores why.'],
};

export type Spotlight =
  | { kind: 'versus'; aId: string; bId: string; prompt: string }
  | { kind: 'balance'; leftId: string; rightId: string; leftLabel: string; rightLabel: string }
  | { kind: 'leader' };

export interface Interpretation {
  question: string;
  options: PeerOption[];
  observation: string;
}

export interface Finding {
  id: string;
  chapterId: string;
  number: string;
  hook: string;
  image: string;
  visual: ChapterVisual;
  spotlight: Spotlight;
  observation: string;
  interpretation: Interpretation;
}

export const findings: Finding[] = [
  {
    id: 'f1',
    chapterId: 'starting-point',
    number: '01',
    hook: 'When planning ovarian stimulation, what really drives the first decision?',
    image: resultsMedia.profiles,
    visual: 'variability',
    spotlight: {
      kind: 'versus',
      aId: 'reserve',
      bId: 'history',
      prompt: 'Measured markers — or observed history?',
    },
    observation:
      'Respondents were close to split between what can be measured before treatment and what a previous cycle revealed.',
    interpretation: {
      question: 'Why do you think specialists prioritise different factors?',
      options: [
        { id: 'populations', label: 'Different patient populations', peerPercent: 37 },
        { id: 'evidence', label: 'Different interpretations of evidence', peerPercent: 26 },
        { id: 'experience', label: 'Differences in clinical experience', peerPercent: 21 },
        { id: 'protocols', label: 'Local protocols and guidelines', peerPercent: 16 },
      ],
      observation: 'Descriptive answers can’t explain why views differ — but they show where the conversation needs to go.',
    },
  },
  {
    id: 'f2',
    chapterId: 'balancing-act',
    number: '02',
    hook: 'Which is the greater concern: too much response or too little?',
    image: resultsMedia.balance,
    visual: 'follicles',
    spotlight: {
      kind: 'balance',
      leftId: 'insufficient',
      rightId: 'excessive',
      leftLabel: 'Too little',
      rightLabel: 'Too much',
    },
    observation:
      'More respondents prioritised avoiding excessive response than insufficient response — yet a quarter pointed to uncertainty itself.',
    interpretation: {
      question: 'What do you believe contributes most to these differences?',
      options: [
        { id: 'patients', label: 'Patient characteristics', peerPercent: 41 },
        { id: 'objectives', label: 'Different treatment objectives', peerPercent: 24 },
        { id: 'experience', label: 'Previous clinical experience', peerPercent: 20 },
        { id: 'clinic', label: 'Clinic practices and protocols', peerPercent: 15 },
      ],
      observation: 'The same clinical objective can involve different considerations for different patients.',
    },
  },
  {
    id: 'f3',
    chapterId: 'next-frontier',
    number: '03',
    hook: 'Where would you make ovarian stimulation simpler?',
    image: resultsMedia.monitoring,
    visual: 'lattice',
    spotlight: { kind: 'leader' },
    observation: 'Opportunities to simplify were spread across the whole treatment journey, not concentrated in one place.',
    interpretation: {
      question: 'Why do you think monitoring remains a challenge for some specialists?',
      options: [
        { id: 'resources', label: 'Time and resource requirements', peerPercent: 38 },
        { id: 'patient', label: 'Practical patient management', peerPercent: 27 },
        { id: 'uncertainty', label: 'Uncertainty about response', peerPercent: 22 },
        { id: 'protocols', label: 'Variations in clinical protocols', peerPercent: 13 },
      ],
      observation: 'Where specialists feel friction today often signals where the next conversation begins.',
    },
  },
];

export interface CompareGroup {
  id: string;
  label: string;
  sufficient: boolean;
  /** Percentage-point offsets applied to each option (in chapter option order). Sum to 0. */
  deltas: number[];
}

export interface CompareDimension {
  id: string;
  label: string;
  groups: CompareGroup[];
}

export const MIN_GROUP_SIZE = 30;

export const compareDimensions: CompareDimension[] = [
  {
    id: 'role',
    label: 'By role',
    groups: [
      { id: 'specialists', label: 'Fertility specialists', sufficient: true, deltas: [3, -1, -1, -1] },
      { id: 'others', label: 'Other professionals', sufficient: true, deltas: [-5, 2, 2, 1] },
    ],
  },
  {
    id: 'experience',
    label: 'By experience',
    groups: [
      { id: 'early', label: 'Under 10 years', sufficient: true, deltas: [-3, 4, 0, -1] },
      { id: 'senior', label: '10+ years', sufficient: true, deltas: [3, -4, 0, 1] },
    ],
  },
  {
    id: 'region',
    label: 'By region',
    groups: [
      { id: 'europe', label: 'Europe', sufficient: true, deltas: [1, -2, 2, -1] },
      { id: 'americas', label: 'Americas', sufficient: true, deltas: [-2, 3, -2, 1] },
      { id: 'apac', label: 'Asia-Pacific', sufficient: false, deltas: [0, 0, 0, 0] },
    ],
  },
];

export const nextPulse = {
  eyebrow: 'Pulse 03 starts here',
  headline: 'The conversation continues.',
  question: 'What should the next IVF Pulse explore?',
  options: [
    { id: 'patient', label: 'Patient experience and communication', peerPercent: 33 },
    { id: 'luteal', label: 'Luteal phase support', peerPercent: 26 },
    { id: 'lab', label: 'Embryo selection and lab practice', peerPercent: 24 },
    { id: 'preservation', label: 'Fertility preservation', peerPercent: 17 },
  ] as PeerOption[],
};

export const signupTopics = [
  'Ovarian stimulation',
  'Patient experience',
  'Luteal phase support',
  'Lab & embryology',
  'Fertility preservation',
];

export const signupFrequencies = [
  { id: 'congress', label: 'Around major congresses' },
  { id: 'quarterly', label: 'Quarterly' },
];
